import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";

// Types
export interface Customer {
  id: number;
  name: string;
  company: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

export interface MixDesign {
  id: number;
  code: string;
  name: string;
  strength: string; // e.g., "3000 PSI", "4000 PSI"
  slump: string; // e.g., "4 inch"
  description: string;
  pricePerYard: number;
  // Material requirements per yard
  materials: {
    materialCode: string;
    quantityPerYard: number;
    unit: string;
  }[];
}

export interface Truck {
  id: number;
  truckNumber: string;
  capacity: number; // yards
  status: 'available' | 'on_delivery' | 'maintenance' | 'out_of_service';
  driver?: string;
  currentLocation?: string;
}

export interface OrderData {
  orderNumber: string;
  // Customer Info
  customerId: number | null;
  customerPO: string;
  // Mix & Quantity
  mixDesignId: number | null;
  totalYards: number;
  yardsPerLoad: number;
  numberOfLoads: number;
  // Delivery
  deliveryDate: string;
  deliveryTime: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  // Contact
  contactName: string;
  contactPhone: string;
  // Notes
  specialInstructions: string;
  // Calculated
  estimatedTotal: number;
}

export interface ExistingOrder {
  id: number;
  orderNumber: string;
  customerName: string;
  company: string;
  mixDesign: string;
  totalYards: number;
  deliveryDate: string;
  deliveryTime: string;
  status: 'scheduled' | 'in_progress' | 'delivered' | 'cancelled';
  estimatedTotal: number;
  loadsCompleted: number;
  totalLoads: number;
}

// Mock Data
const MOCK_CUSTOMERS: Customer[] = [
  { id: 1, name: 'John Smith', company: 'Smith Construction', phone: '555-0101', email: 'john@smithconstruction.com', address: '123 Main St', city: 'Austin', state: 'TX', zip: '78701' },
  { id: 2, name: 'Maria Garcia', company: 'Garcia Builders', phone: '555-0102', email: 'maria@garciabuilders.com', address: '456 Oak Ave', city: 'Austin', state: 'TX', zip: '78702' },
  { id: 3, name: 'Robert Johnson', company: 'Johnson & Sons', phone: '555-0103', email: 'robert@johnsonandsons.com', address: '789 Pine Rd', city: 'Round Rock', state: 'TX', zip: '78664' },
  { id: 4, name: 'Sarah Williams', company: 'Williams Contractors', phone: '555-0104', email: 'sarah@williamscontractors.com', address: '321 Elm St', city: 'Cedar Park', state: 'TX', zip: '78613' },
  { id: 5, name: 'Michael Brown', company: 'Brown Development', phone: '555-0105', email: 'michael@browndev.com', address: '654 Maple Dr', city: 'Georgetown', state: 'TX', zip: '78626' },
];

const MOCK_MIX_DESIGNS: MixDesign[] = [
  {
    id: 1,
    code: 'MIX-3000',
    name: '3000 PSI Standard',
    strength: '3000 PSI',
    slump: '4 inch',
    description: 'Standard residential mix for driveways, sidewalks, patios',
    pricePerYard: 125,
    materials: [
      { materialCode: 'CEM-T1', quantityPerYard: 0.25, unit: 'tons' },
      { materialCode: 'AGG-SAND', quantityPerYard: 0.45, unit: 'tons' },
      { materialCode: 'AGG-GRAVEL', quantityPerYard: 0.55, unit: 'tons' },
      { materialCode: 'LIQ-WATER', quantityPerYard: 35, unit: 'gallons' },
      { materialCode: 'ADM-AIR', quantityPerYard: 0.5, unit: 'gallons' },
    ],
  },
  {
    id: 2,
    code: 'MIX-4000',
    name: '4000 PSI Commercial',
    strength: '4000 PSI',
    slump: '4 inch',
    description: 'Commercial grade for foundations, slabs, structural',
    pricePerYard: 145,
    materials: [
      { materialCode: 'CEM-T1', quantityPerYard: 0.30, unit: 'tons' },
      { materialCode: 'AGG-SAND', quantityPerYard: 0.40, unit: 'tons' },
      { materialCode: 'AGG-GRAVEL', quantityPerYard: 0.60, unit: 'tons' },
      { materialCode: 'LIQ-WATER', quantityPerYard: 32, unit: 'gallons' },
      { materialCode: 'ADM-WR', quantityPerYard: 0.8, unit: 'gallons' },
    ],
  },
  {
    id: 3,
    code: 'MIX-4500',
    name: '4500 PSI High Strength',
    strength: '4500 PSI',
    slump: '5 inch',
    description: 'High strength for commercial/industrial applications',
    pricePerYard: 165,
    materials: [
      { materialCode: 'CEM-T1', quantityPerYard: 0.35, unit: 'tons' },
      { materialCode: 'CEM-FA', quantityPerYard: 0.05, unit: 'tons' },
      { materialCode: 'AGG-SAND', quantityPerYard: 0.38, unit: 'tons' },
      { materialCode: 'AGG-STONE', quantityPerYard: 0.58, unit: 'tons' },
      { materialCode: 'LIQ-WATER', quantityPerYard: 30, unit: 'gallons' },
      { materialCode: 'ADM-SP', quantityPerYard: 1.0, unit: 'gallons' },
    ],
  },
  {
    id: 4,
    code: 'MIX-5000',
    name: '5000 PSI Heavy Duty',
    strength: '5000 PSI',
    slump: '5 inch',
    description: 'Heavy duty for industrial floors, parking structures',
    pricePerYard: 185,
    materials: [
      { materialCode: 'CEM-T3', quantityPerYard: 0.38, unit: 'tons' },
      { materialCode: 'CEM-FA', quantityPerYard: 0.08, unit: 'tons' },
      { materialCode: 'AGG-SAND', quantityPerYard: 0.35, unit: 'tons' },
      { materialCode: 'AGG-STONE', quantityPerYard: 0.62, unit: 'tons' },
      { materialCode: 'LIQ-WATER', quantityPerYard: 28, unit: 'gallons' },
      { materialCode: 'ADM-SP', quantityPerYard: 1.5, unit: 'gallons' },
      { materialCode: 'ADM-ACC', quantityPerYard: 0.5, unit: 'gallons' },
    ],
  },
  {
    id: 5,
    code: 'MIX-PUMP',
    name: 'Pump Mix 4000',
    strength: '4000 PSI',
    slump: '6 inch',
    description: 'Pumpable mix for hard-to-reach areas',
    pricePerYard: 160,
    materials: [
      { materialCode: 'CEM-T1', quantityPerYard: 0.32, unit: 'tons' },
      { materialCode: 'AGG-SAND', quantityPerYard: 0.48, unit: 'tons' },
      { materialCode: 'AGG-GRAVEL', quantityPerYard: 0.52, unit: 'tons' },
      { materialCode: 'LIQ-WATER', quantityPerYard: 38, unit: 'gallons' },
      { materialCode: 'ADM-WR', quantityPerYard: 1.2, unit: 'gallons' },
      { materialCode: 'ADM-AIR', quantityPerYard: 0.6, unit: 'gallons' },
    ],
  },
];

const MOCK_TRUCKS: Truck[] = [
  { id: 1, truckNumber: 'T-101', capacity: 10, status: 'available', driver: 'Mike Wilson', currentLocation: 'Yard' },
  { id: 2, truckNumber: 'T-102', capacity: 10, status: 'available', driver: 'Tom Davis', currentLocation: 'Yard' },
  { id: 3, truckNumber: 'T-103', capacity: 10, status: 'on_delivery', driver: 'James Miller', currentLocation: 'En route - Downtown' },
  { id: 4, truckNumber: 'T-104', capacity: 8, status: 'available', driver: 'Chris Taylor', currentLocation: 'Yard' },
  { id: 5, truckNumber: 'T-105', capacity: 10, status: 'maintenance', driver: null, currentLocation: 'Shop' },
  { id: 6, truckNumber: 'T-106', capacity: 10, status: 'available', driver: 'Steve Anderson', currentLocation: 'Yard' },
  { id: 7, truckNumber: 'T-107', capacity: 8, status: 'on_delivery', driver: 'Paul Martinez', currentLocation: 'En route - North Austin' },
  { id: 8, truckNumber: 'T-108', capacity: 10, status: 'available', driver: 'Kevin Thomas', currentLocation: 'Yard' },
];

const MOCK_EXISTING_ORDERS: ExistingOrder[] = [
  { id: 1, orderNumber: 'ORD-241209-A1B2', customerName: 'John Smith', company: 'Smith Construction', mixDesign: '3000 PSI Standard', totalYards: 30, deliveryDate: '2024-12-10', deliveryTime: '07:00', status: 'scheduled', estimatedTotal: 3750, loadsCompleted: 0, totalLoads: 3 },
  { id: 2, orderNumber: 'ORD-241209-C3D4', customerName: 'Maria Garcia', company: 'Garcia Builders', mixDesign: '4000 PSI Commercial', totalYards: 50, deliveryDate: '2024-12-10', deliveryTime: '08:00', status: 'in_progress', estimatedTotal: 7250, loadsCompleted: 2, totalLoads: 5 },
  { id: 3, orderNumber: 'ORD-241208-E5F6', customerName: 'Robert Johnson', company: 'Johnson & Sons', mixDesign: '5000 PSI High Strength', totalYards: 20, deliveryDate: '2024-12-09', deliveryTime: '06:30', status: 'delivered', estimatedTotal: 3400, loadsCompleted: 2, totalLoads: 2 },
  { id: 4, orderNumber: 'ORD-241210-G7H8', customerName: 'Sarah Williams', company: 'Williams Contractors', mixDesign: '3000 PSI Standard', totalYards: 15, deliveryDate: '2024-12-11', deliveryTime: '09:00', status: 'scheduled', estimatedTotal: 1875, loadsCompleted: 0, totalLoads: 2 },
  { id: 5, orderNumber: 'ORD-241207-I9J0', customerName: 'Michael Brown', company: 'Brown Development', mixDesign: 'Pump Mix 4000', totalYards: 80, deliveryDate: '2024-12-08', deliveryTime: '05:30', status: 'delivered', estimatedTotal: 12800, loadsCompleted: 8, totalLoads: 8 },
  { id: 6, orderNumber: 'ORD-241210-K1L2', customerName: 'John Smith', company: 'Smith Construction', mixDesign: '4000 PSI Commercial', totalYards: 40, deliveryDate: '2024-12-11', deliveryTime: '07:30', status: 'scheduled', estimatedTotal: 5800, loadsCompleted: 0, totalLoads: 4 },
];

export interface MaterialAvailability {
  materialCode: string;
  materialName: string;
  required: number;
  available: number;
  unit: string;
  sufficient: boolean;
  shortage: number;
}

class CustomerDeliverySchedulingWorkflowStore {
  rootStore: RootStore;

  // Mock Data
  customers: Customer[] = [...MOCK_CUSTOMERS];
  mixDesigns: MixDesign[] = [...MOCK_MIX_DESIGNS];
  trucks: Truck[] = [...MOCK_TRUCKS];
  existingOrders: ExistingOrder[] = [...MOCK_EXISTING_ORDERS];

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Order Data
  orderData: OrderData = {
    orderNumber: '',
    customerId: null,
    customerPO: '',
    mixDesignId: null,
    totalYards: 0,
    yardsPerLoad: 10,
    numberOfLoads: 0,
    deliveryDate: new Date().toISOString().split('T')[0],
    deliveryTime: '08:00',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryZip: '',
    contactName: '',
    contactPhone: '',
    specialInstructions: '',
    estimatedTotal: 0,
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.orderData.orderNumber = this.generateOrderNumber();
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultOrderData = (): OrderData => ({
    orderNumber: this.generateOrderNumber(),
    customerId: null,
    customerPO: '',
    mixDesignId: null,
    totalYards: 0,
    yardsPerLoad: 10,
    numberOfLoads: 0,
    deliveryDate: new Date().toISOString().split('T')[0],
    deliveryTime: '08:00',
    deliveryAddress: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryZip: '',
    contactName: '',
    contactPhone: '',
    specialInstructions: '',
    estimatedTotal: 0,
  });

  generateOrderNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    return `ORD-${year}${month}${day}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
  };

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep++;
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateOrderData = <K extends keyof OrderData>(field: K, value: OrderData[K]) => {
    this.orderData[field] = value;

    // Auto-calculate number of loads
    if (field === 'totalYards' || field === 'yardsPerLoad') {
      if (this.orderData.yardsPerLoad > 0) {
        this.orderData.numberOfLoads = Math.ceil(
          this.orderData.totalYards / this.orderData.yardsPerLoad
        );
      }
    }

    // Auto-calculate estimated total
    if (field === 'totalYards' || field === 'mixDesignId') {
      const mix = this.selectedMixDesign;
      if (mix && this.orderData.totalYards > 0) {
        this.orderData.estimatedTotal = mix.pricePerYard * this.orderData.totalYards;
      }
    }

    // Auto-fill customer contact info
    if (field === 'customerId') {
      const customer = this.selectedCustomer;
      if (customer) {
        this.orderData.contactName = customer.name;
        this.orderData.contactPhone = customer.phone;
        this.orderData.deliveryAddress = customer.address;
        this.orderData.deliveryCity = customer.city;
        this.orderData.deliveryState = customer.state;
        this.orderData.deliveryZip = customer.zip;
      }
    }
  };

  selectCustomer = (customerId: number | null) => {
    this.updateOrderData('customerId', customerId);
  };

  selectMixDesign = (mixDesignId: number | null) => {
    this.updateOrderData('mixDesignId', mixDesignId);
  };

  // Load initial data (simulated delay for demo)
  loadInitialData = async () => {
    this.loading = true;
    await new Promise(resolve => setTimeout(resolve, 300));
    runInAction(() => {
      this.loading = false;
    });
  };

  // Submit order
  submitOrder = async () => {
    this.loading = true;
    this.error = null;

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      runInAction(() => {
        // Add to existing orders
        const newOrder: ExistingOrder = {
          id: this.existingOrders.length + 1,
          orderNumber: this.orderData.orderNumber,
          customerName: this.selectedCustomer?.name || '',
          company: this.selectedCustomer?.company || '',
          mixDesign: this.selectedMixDesign?.name || '',
          totalYards: this.orderData.totalYards,
          deliveryDate: this.orderData.deliveryDate,
          deliveryTime: this.orderData.deliveryTime,
          status: 'scheduled',
          estimatedTotal: this.orderData.estimatedTotal,
          loadsCompleted: 0,
          totalLoads: this.orderData.numberOfLoads,
        };
        this.existingOrders.unshift(newOrder);

        this.success = `Order ${this.orderData.orderNumber} has been created successfully! ${this.orderData.totalYards} yards of ${this.selectedMixDesign?.name} scheduled for delivery on ${this.orderData.deliveryDate}.`;
        this.loading = false;
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || 'Failed to create order';
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.orderData = this.getDefaultOrderData();
  };

  // Computed: Selected customer
  get selectedCustomer(): Customer | null {
    if (!this.orderData.customerId) return null;
    return this.customers.find(c => c.id === this.orderData.customerId) || null;
  }

  // Computed: Selected mix design
  get selectedMixDesign(): MixDesign | null {
    if (!this.orderData.mixDesignId) return null;
    return this.mixDesigns.find(m => m.id === this.orderData.mixDesignId) || null;
  }

  // Computed: Material availability check
  get materialAvailability(): MaterialAvailability[] {
    const mix = this.selectedMixDesign;
    if (!mix || this.orderData.totalYards <= 0) return [];

    // Get raw materials from the AddRawMaterials workflow store
    const rawMaterialsStore = this.rootStore.addRawMaterialsWorkflowStore as any;
    const rawMaterials = rawMaterialsStore?.materials || [];

    return mix.materials.map(req => {
      const material = rawMaterials.find((m: any) => m.materialCode === req.materialCode);
      const required = req.quantityPerYard * this.orderData.totalYards;
      const available = material?.currentQuantity || 0;
      const sufficient = available >= required;
      const shortage = sufficient ? 0 : required - available;

      return {
        materialCode: req.materialCode,
        materialName: material?.name || req.materialCode,
        required: Math.round(required * 100) / 100,
        available,
        unit: req.unit,
        sufficient,
        shortage: Math.round(shortage * 100) / 100,
      };
    });
  }

  // Computed: Can fulfill order (all materials available)
  get canFulfillOrder(): boolean {
    if (this.materialAvailability.length === 0) return false;
    return this.materialAvailability.every(m => m.sufficient);
  }

  // Computed: Materials with shortage
  get materialsWithShortage(): MaterialAvailability[] {
    return this.materialAvailability.filter(m => !m.sufficient);
  }

  // Computed: Available trucks
  get availableTrucks(): Truck[] {
    return this.trucks.filter(t => t.status === 'available');
  }

  // Computed: Trucks needed for this order
  get trucksNeeded(): number {
    return this.orderData.numberOfLoads;
  }

  // Computed: Has enough truck capacity
  get hasEnoughTruckCapacity(): boolean {
    return this.availableTrucks.length >= this.trucksNeeded;
  }

  // Computed: Total available capacity
  get totalAvailableCapacity(): number {
    return this.availableTrucks.reduce((sum, t) => sum + t.capacity, 0);
  }

  // Computed: Fleet summary
  get fleetSummary() {
    return {
      total: this.trucks.length,
      available: this.trucks.filter(t => t.status === 'available').length,
      onDelivery: this.trucks.filter(t => t.status === 'on_delivery').length,
      maintenance: this.trucks.filter(t => t.status === 'maintenance').length,
      outOfService: this.trucks.filter(t => t.status === 'out_of_service').length,
    };
  }

  // Computed: Orders by status
  get ordersByStatus() {
    return {
      scheduled: this.existingOrders.filter(o => o.status === 'scheduled'),
      inProgress: this.existingOrders.filter(o => o.status === 'in_progress'),
      delivered: this.existingOrders.filter(o => o.status === 'delivered'),
      cancelled: this.existingOrders.filter(o => o.status === 'cancelled'),
    };
  }

  // Computed: Today's orders
  get todaysOrders(): ExistingOrder[] {
    const today = new Date().toISOString().split('T')[0];
    return this.existingOrders.filter(o => o.deliveryDate === today);
  }

  // Computed: Order stats
  get orderStats() {
    const scheduled = this.ordersByStatus.scheduled;
    const inProgress = this.ordersByStatus.inProgress;
    return {
      totalOrders: this.existingOrders.length,
      scheduledCount: scheduled.length,
      inProgressCount: inProgress.length,
      scheduledYards: scheduled.reduce((sum, o) => sum + o.totalYards, 0),
      inProgressYards: inProgress.reduce((sum, o) => sum + o.totalYards, 0),
      totalRevenue: this.existingOrders.reduce((sum, o) => sum + o.estimatedTotal, 0),
    };
  }

  // Validation
  get canProceedFromStep1(): boolean {
    return !!this.orderData.customerId;
  }

  get canProceedFromStep2(): boolean {
    return !!this.orderData.mixDesignId && this.orderData.totalYards > 0;
  }

  get canProceedFromStep3(): boolean {
    return (
      !!this.orderData.deliveryDate &&
      !!this.orderData.deliveryTime &&
      !!this.orderData.deliveryAddress &&
      !!this.orderData.contactName &&
      !!this.orderData.contactPhone
    );
  }
}

export default CustomerDeliverySchedulingWorkflowStore;
